import { useMemo, useState } from 'react';
import {
  Archive,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Download,
  Folder,
  Link,
} from 'react-feather';

import { Card } from '~ui/base/card';
import { ProgressRing } from '~ui/base/progress-ring';
import { Tooltip } from '~ui/base/tooltip';
import { Typography } from '~ui/base/typography';
import { cn } from '~lib/utils/style';
import { formatBytes } from '~lib/utils/format-bytes';
import { useDownloadBlob } from '~lib/storage/use-download-blob';
import { useDownloadDataset } from '~lib/storage/use-download-dataset';
import { datasetDownloadSupported } from '~lib/storage/download-dataset';
import type { Dataset, DatasetFile } from '~lib/storage/datasets';

interface DatasetsTableProps {
  datasets: Dataset[];
  // Opens the temporary-link dialog for a dataset's zip. Owned by the page,
  // like the upload dialog, so the table stays a plain list.
  onCreateLink: (dataset: Dataset) => void;
}

// Fixed-width metric columns so the "Size" cell and the action icons column
// align horizontally between the dataset row and the nested file rows. Col 3
// is wide enough to fit three 32px icon buttons with small gaps (dataset rows
// get a link + zip + folder trio; file rows put their single download in the
// same right-aligned slot, which keeps the rightmost-action column perfectly
// vertical across the table).
const ROW_GRID = 'grid grid-cols-[minmax(0,1fr)_5rem_7rem] items-center gap-x-8';

type SortKey = 'name' | 'size';
type SortDirection = 'asc' | 'desc';

function DatasetsTable({ datasets, onCreateLink }: DatasetsTableProps) {
  const fileDownloader = useDownloadBlob();
  const datasetDownloader = useDownloadDataset();
  const folderSupported = datasetDownloadSupported();
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const anyError = datasetDownloader.error ?? fileDownloader.error;

  const sortedDatasets = useMemo(() => {
    const copy = [...datasets];
    const factor = sortDirection === 'asc' ? 1 : -1;
    copy.sort((a, b) => {
      if (sortKey === 'name') return a.name.localeCompare(b.name) * factor;
      return (displayBytes(a) - displayBytes(b)) * factor;
    });
    return copy;
  }, [datasets, sortKey, sortDirection]);

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  return (
    <Card ornament="outline" className="overflow-hidden p-0">
      <div
        className={cn(
          ROW_GRID,
          'border-b border-border bg-card-highlight/60 px-6 py-3',
        )}
      >
        <SortableHeader
          active={sortKey === 'name'}
          direction={sortDirection}
          onClick={() => handleSort('name')}
        >
          Dataset
        </SortableHeader>
        <SortableHeader
          active={sortKey === 'size'}
          direction={sortDirection}
          onClick={() => handleSort('size')}
          align="right"
        >
          Size
        </SortableHeader>
        <span />
      </div>
      <ul className="divide-y divide-border">
        {sortedDatasets.map((dataset) => (
          <DatasetRow
            key={dataset.name}
            dataset={dataset}
            downloadingPath={fileDownloader.downloading}
            fileBytesDownloaded={fileDownloader.bytesDownloaded}
            fileTotalBytes={fileDownloader.totalBytes}
            onFileDownload={fileDownloader.download}
            isDatasetDownloading={datasetDownloader.downloadingName === dataset.name}
            anyDatasetDownloading={datasetDownloader.downloadingName !== null}
            datasetBytesDownloaded={
              datasetDownloader.downloadingName === dataset.name
                ? datasetDownloader.bytesDownloaded
                : 0
            }
            onDatasetDownload={datasetDownloader.download}
            folderSupported={folderSupported}
            onCreateLink={onCreateLink}
          />
        ))}
      </ul>
      {anyError && (
        <div className="border-t border-destructive/30 bg-destructive-container px-6 py-3 text-destructive-container-foreground">
          <Typography variant="body-md">
            Download failed: {anyError.message}
          </Typography>
        </div>
      )}
    </Card>
  );
}

interface SortableHeaderProps {
  children: React.ReactNode;
  active: boolean;
  direction: SortDirection;
  onClick: () => void;
  align?: 'left' | 'right';
}

function SortableHeader({
  children,
  active,
  direction,
  onClick,
  align = 'left',
}: SortableHeaderProps) {
  const Indicator = direction === 'asc' ? ChevronUp : ChevronDown;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-sort={active ? (direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={cn(
        'inline-flex items-center gap-x-1 rounded-sm transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        align === 'right' ? 'justify-end justify-self-end' : 'justify-start justify-self-start',
        active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
      )}
    >
      <Typography variant="overline" as="span">
        {children}
      </Typography>
      <Indicator
        size={12}
        aria-hidden
        className={cn('shrink-0', active ? 'opacity-100' : 'opacity-0')}
      />
    </button>
  );
}

interface DatasetRowProps {
  dataset: Dataset;
  downloadingPath: string | null;
  fileBytesDownloaded: number;
  fileTotalBytes: number | null;
  onFileDownload: (fullPath: string, knownSize?: number) => void;
  isDatasetDownloading: boolean;
  anyDatasetDownloading: boolean;
  datasetBytesDownloaded: number;
  onDatasetDownload: (dataset: Dataset) => void;
  folderSupported: boolean;
  onCreateLink: (dataset: Dataset) => void;
}

function DatasetRow({
  dataset,
  downloadingPath,
  fileBytesDownloaded,
  fileTotalBytes,
  onFileDownload,
  isDatasetDownloading,
  anyDatasetDownloading,
  datasetBytesDownloaded,
  onDatasetDownload,
  folderSupported,
  onCreateLink,
}: DatasetRowProps) {
  const [expanded, setExpanded] = useState(false);

  const zipOnly = isZipOnly(dataset);
  const sizeBytes = displayBytes(dataset);
  const expandable = dataset.files.length > 0;

  const folderDisabled =
    !folderSupported ||
    dataset.files.length === 0 ||
    (anyDatasetDownloading && !isDatasetDownloading);
  const folderTitle = !folderSupported
    ? 'Saving to a folder requires Chrome, Edge, or Opera. Expand the row to download files individually.'
    : dataset.files.length === 0
      ? 'No loose files to download.'
      : isDatasetDownloading
        ? `Downloading… ${formatBytes(datasetBytesDownloaded)} of ${formatBytes(dataset.totalBytes)}`
        : `Download all ${dataset.fileCount.toLocaleString()} files (${formatBytes(dataset.totalBytes)})`;
  const folderPercent = isDatasetDownloading
    ? percentOf(datasetBytesDownloaded, dataset.totalBytes)
    : null;

  const zipPath = dataset.zipFile?.fullPath;
  const zipDownloading = zipPath !== undefined && downloadingPath === zipPath;
  const zipDisabled = !dataset.zipFile || zipDownloading;
  const zipTitle = !dataset.zipFile
    ? `No archive uploaded yet. Add ${dataset.name}/${dataset.name}.zip to enable.`
    : `Download ${dataset.name}.zip (${formatBytes(dataset.zipFile.size)})`;
  const zipPercent = zipDownloading
    ? percentOf(fileBytesDownloaded, fileTotalBytes ?? dataset.zipFile?.size ?? null)
    : null;

  const linkTitle = !dataset.zipFile
    ? `No archive uploaded yet. Add ${dataset.name}/${dataset.name}.zip to enable temporary links.`
    : `Create a temporary link to ${dataset.name}.zip`;

  return (
    <li>
      <div
        className={cn(
          ROW_GRID,
          'px-6 py-4 transition-colors hover:bg-card-highlight',
        )}
      >
        {expandable ? (
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
            className="col-span-2 grid grid-cols-subgrid items-center gap-x-8 text-left focus-visible:outline-none"
          >
            <DatasetLabel
              dataset={dataset}
              zipOnly={zipOnly}
              chevron={
                <ChevronRight
                  size={16}
                  className={cn(
                    'shrink-0 text-muted-foreground transition-transform duration-200',
                    expanded && 'rotate-90',
                  )}
                  aria-hidden
                />
              }
            />
            <Typography variant="body-md" as="span" className="text-right tabular-nums">
              {formatBytes(sizeBytes)}
            </Typography>
          </button>
        ) : (
          <div className="col-span-2 grid grid-cols-subgrid items-center gap-x-8">
            {/* size-4 + gap matches the chevron slot so dataset names stay
                vertically aligned whether or not the row is expandable. */}
            <DatasetLabel
              dataset={dataset}
              zipOnly={zipOnly}
              chevron={<span aria-hidden className="size-4 shrink-0" />}
            />
            <Typography variant="body-md" as="span" className="text-right tabular-nums">
              {formatBytes(sizeBytes)}
            </Typography>
          </div>
        )}
        <div className="flex items-center justify-end gap-x-1">
          <Tooltip content={linkTitle}>
            <span className="inline-flex">
              <IconActionButton
                onClick={() => onCreateLink(dataset)}
                disabled={!dataset.zipFile}
                aria-label={linkTitle}
                active={false}
                percent={null}
              >
                <Link size={16} aria-hidden />
              </IconActionButton>
            </span>
          </Tooltip>
          <Tooltip content={zipTitle}>
            {/* span wrapper so the disabled button's pointer-events:none
                doesn't swallow the hover event Radix needs */}
            <span className="inline-flex">
              <IconActionButton
                onClick={() => zipPath && onFileDownload(zipPath, dataset.zipFile?.size)}
                disabled={zipDisabled}
                aria-label={zipTitle}
                active={zipDownloading}
                percent={zipPercent}
              >
                <Archive size={16} aria-hidden />
              </IconActionButton>
            </span>
          </Tooltip>
          <Tooltip content={folderTitle}>
            <span className="inline-flex">
              <IconActionButton
                onClick={() => onDatasetDownload(dataset)}
                disabled={folderDisabled}
                aria-label={folderTitle}
                active={isDatasetDownloading}
                percent={folderPercent}
              >
                <Folder size={16} aria-hidden />
              </IconActionButton>
            </span>
          </Tooltip>
        </div>
      </div>
      {expanded && (
        <FileList
          files={dataset.files}
          downloadingPath={downloadingPath}
          fileBytesDownloaded={fileBytesDownloaded}
          fileTotalBytes={fileTotalBytes}
          onDownload={onFileDownload}
        />
      )}
    </li>
  );
}

interface DatasetLabelProps {
  dataset: Dataset;
  zipOnly: boolean;
  chevron: React.ReactNode;
}

function DatasetLabel({ dataset, zipOnly, chevron }: DatasetLabelProps) {
  return (
    <div className="flex items-center gap-x-3 overflow-hidden">
      {chevron}
      <div className="flex flex-col gap-y-1 overflow-hidden">
        <Typography variant="subtitle-sm" as="span" className="truncate">
          {dataset.name}
        </Typography>
        <Typography
          variant="caption-sm"
          as="span"
          className="tabular-nums text-muted-foreground"
        >
          {zipOnly
            ? '.zip only'
            : `${dataset.fileCount.toLocaleString()} ${dataset.fileCount === 1 ? 'file' : 'files'}`}
        </Typography>
      </div>
    </div>
  );
}

interface FileListProps {
  files: DatasetFile[];
  downloadingPath: string | null;
  fileBytesDownloaded: number;
  fileTotalBytes: number | null;
  onDownload: (fullPath: string, knownSize?: number) => void;
}

function FileList({
  files,
  downloadingPath,
  fileBytesDownloaded,
  fileTotalBytes,
  onDownload,
}: FileListProps) {
  return (
    <ul className="divide-y divide-border bg-card-highlight/40">
      {files.map((file) => {
        const downloading = downloadingPath === file.fullPath;
        return (
          <FileRow
            key={file.fullPath}
            file={file}
            downloading={downloading}
            percent={
              downloading
                ? percentOf(fileBytesDownloaded, fileTotalBytes ?? file.size)
                : null
            }
            onDownload={onDownload}
          />
        );
      })}
    </ul>
  );
}

interface FileRowProps {
  file: DatasetFile;
  downloading: boolean;
  percent: number | null;
  onDownload: (fullPath: string, knownSize?: number) => void;
}

function FileRow({ file, downloading, percent, onDownload }: FileRowProps) {
  return (
    <li className={cn(ROW_GRID, 'px-6 py-2.5')}>
      <Typography
        variant="body-md"
        as="span"
        // pl-7 = 16px chevron + 12px gap from the parent dataset row, so the
        // file path lines up underneath the dataset name.
        className="truncate pl-7 font-mono text-foreground/90"
      >
        {file.path}
      </Typography>
      <Typography
        variant="body-md"
        as="span"
        className="text-right tabular-nums text-muted-foreground"
      >
        {formatBytes(file.size)}
      </Typography>
      <Tooltip content={`Download ${file.path}`}>
        <span className="ml-auto inline-flex">
          <IconActionButton
            onClick={() => onDownload(file.fullPath, file.size)}
            disabled={downloading}
            aria-label={`Download ${file.path}`}
            active={downloading}
            percent={percent}
          >
            <Download size={16} aria-hidden />
          </IconActionButton>
        </span>
      </Tooltip>
    </li>
  );
}

interface IconActionButtonProps {
  onClick: () => void;
  disabled?: boolean;
  'aria-label': string;
  active: boolean;
  // null = active but progress unknown (falls back to a pulse animation).
  // number = active with a known 0–100 percent (renders the ring).
  percent: number | null;
  children: React.ReactNode;
}

// 32px circular button + optional progress ring overlay. Centralises the
// download-button visuals so the zip / folder / per-file buttons all match.
function IconActionButton({
  onClick,
  disabled,
  active,
  percent,
  children,
  ...aria
}: IconActionButtonProps) {
  const hasRingProgress = active && percent !== null;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={aria['aria-label']}
      className={cn(
        'relative inline-flex size-8 shrink-0 items-center justify-center rounded-full',
        'transition-colors',
        // The text colour switches to primary while a download is running so
        // the progress ring (which inherits currentColor) reads as the brand
        // blue; the icon turns blue too which subtly reinforces "active".
        active ? 'text-primary' : 'text-muted-foreground hover:text-primary',
        'hover:bg-card',
        'focus-visible:bg-card focus-visible:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        'disabled:pointer-events-none disabled:opacity-40',
      )}
    >
      {hasRingProgress && <ProgressRing percent={percent} />}
      <span
        className={cn(
          'relative inline-flex',
          active && !hasRingProgress && 'animate-pulse',
        )}
      >
        {children}
      </span>
    </button>
  );
}

function percentOf(numerator: number, denominator: number | null): number | null {
  if (denominator == null || denominator <= 0) return null;
  return (numerator / denominator) * 100;
}

// A dataset whose only blob is the packaged `<name>.zip`. The aggregator
// excludes the zip from `files`/`totalBytes`, so this surface state would
// otherwise read as "0 files, 0 B" even though the archive is right there.
function isZipOnly(dataset: Dataset): boolean {
  return dataset.fileCount === 0 && dataset.zipFile !== null;
}

function displayBytes(dataset: Dataset): number {
  return isZipOnly(dataset) ? dataset.zipFile!.size : dataset.totalBytes;
}

export { DatasetsTable };
